import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Compass, Check, Circle, MoreHorizontal, ArrowRight, Sparkles, Pencil } from 'lucide-react';
import Header from '../components/Header';
import CompanionCard from '../components/advisor/CompanionCard';
import DecisionQualityPanel from '../components/advisor/DecisionQualityPanel';
import FirmPickCard from '../components/advisor/FirmPickCard';
import SEO from '../components/SEO';
import advisor from '../lib/advisor';
import { API_BASE, api } from '../lib/api';

/**
 * "Mi decisión" — Decision Workspace.
 *
 * No es un historial. No es un dashboard. Es un espacio donde el usuario
 * comprende su proceso y toma decisiones con mayor claridad.
 */

const _STATUS_ICON = {
  defined: <Check className="w-4 h-4 text-emerald-600" />,
  exploring: <MoreHorizontal className="w-4 h-4 text-amber-600" />,
  undefined: <Circle className="w-3.5 h-3.5 text-gray-300" />,
};

// Aspectos editables desde el Advisor guiado. La key coincide con el
// mapa DEEP_LINK_TURN_KEY_MAP de AdvisorGuidedStart.
const _EDITABLE_ASPECTS = new Set(['city', 'operation', 'type']);

function ProgressList({ items, onEdit }) {
  return (
    <ul className="space-y-2.5" data-testid="workspace-progress">
      {items.map((a) => {
        const editable = _EDITABLE_ASPECTS.has(a.key);
        return (
          <li key={a.key} className="group flex items-start gap-2.5">
            <span className="mt-0.5 shrink-0">{_STATUS_ICON[a.status]}</span>
            <span className={`flex-1 text-sm leading-relaxed ${a.status === 'defined' ? 'text-habita-dark' : 'text-gray-600'}`}>
              {a.human_readable}
            </span>
            {editable && (
              <button
                type="button"
                onClick={() => onEdit(a.key)}
                data-testid={`workspace-edit-${a.key}`}
                className="opacity-0 group-hover:opacity-100 focus:opacity-100 inline-flex items-center gap-1 text-[11px] text-habita hover:text-habita-dark transition-opacity"
                aria-label={`Editar ${a.human_readable}`}
                title="Refinar con el Advisor"
              >
                <Pencil className="w-3 h-3" />
                Editar
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function Timeline({ phases }) {
  if (!phases?.length) return null;
  return (
    <div className="space-y-3" data-testid="workspace-timeline">
      {phases.map((p, i) => (
        <div key={i} className="relative pl-6">
          <span className="absolute left-0 top-1.5 w-2.5 h-2.5 rounded-full bg-habita" />
          {i < phases.length - 1 && (
            <span className="absolute left-[4px] top-4 w-[2px] h-full bg-habita/20" />
          )}
          <p className="text-[10px] uppercase tracking-widest text-habita-dark/70 font-semibold">{p.label}</p>
          <p className="text-[15px] text-habita-dark leading-relaxed mt-0.5">{p.narrative}</p>
        </div>
      ))}
    </div>
  );
}

async function _reportEvent(event, metadata) {
  const ctx = advisor.getActiveContextId();
  if (!ctx) return;
  try {
    await fetch(`${API_BASE}/advisor/workspace/${ctx}/events`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, metadata }),
    });
  } catch { /* silencioso */ }
}

export default function DecisionWorkspacePage() {
  const navigate = useNavigate();
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companionPrompt, setCompanionPrompt] = useState(null);
  const [quality, setQuality] = useState(null);
  const [firmPick, setFirmPick] = useState(null);
  const [advisorMemory, setAdvisorMemory] = useState(null);

  const _loadFirmPick = async (ctxId) => {
    if (!ctxId) return;
    try {
      const fp = await api.latestFirmPick(ctxId);
      setFirmPick(fp);
    } catch { setFirmPick(null); }
  };

  const _handleDeleteFirmPick = async () => {
    const ctxId = advisor.getActiveContextId();
    if (!ctxId) return;
    await api.deleteFirmPick(ctxId);
    setFirmPick(null);
  };

  const _onEditAspect = (aspectKey) => {
    _reportEvent('progress_interacted', { aspect: aspectKey, action: 'edit_via_advisor' });
    navigate(`/advisor/inicio?turn=${encodeURIComponent(aspectKey)}`);
  };

  const _loadCompanion = async () => {
    const data = await advisor.getCompanionPrompts();
    if (!data) { setCompanionPrompt(null); return; }
    const primary = (data.prompts || []).find(
      (p) => p.id === data.primary_prompt_id,
    ) || (data.prompts || [])[0] || null;
    setCompanionPrompt(primary);
  };

  const _loadQuality = async () => {
    const q = await advisor.getDecisionQuality({ recordView: true });
    setQuality(q);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Asegura Context (cross-session ACT — puede restaurar uno previo)
      const ctx = await advisor.getOrStartContext({ entryPoint: 'other', intent: 'mi decisión' });
      const ctxId = advisor.getActiveContextId();
      // Si el ctx fue restaurado de sesión previa, guardamos el flag
      if (ctx?.restored) setAdvisorMemory({ stale_days: ctx.stale_days ?? null });
      if (!ctxId) { setLoading(false); return; }
      // Carga firm pick + companion + quality en paralelo con workspace
      _loadFirmPick(ctxId);
      try {
        const r = await fetch(`${API_BASE}/advisor/workspace/${ctxId}`);
        if (r.ok) {
          const data = await r.json();
          if (!cancelled) setWs(data);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
      _reportEvent('workspace_opened');
      // Cargar prompts del companion en paralelo
      if (!cancelled) {
        await Promise.all([_loadCompanion(), _loadQuality()]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const _onCompanionResolved = async () => {
    setCompanionPrompt(null);
    await _loadCompanion();
    // Un cambio en preferencias puede modificar la calidad del proceso.
    await _loadQuality();
    const ctxId = advisor.getActiveContextId();
    if (ctxId) {
      try {
        const r = await fetch(`${API_BASE}/advisor/workspace/${ctxId}`);
        if (r.ok) setWs(await r.json());
      } catch { /* silencioso */ }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-10">
          <div className="h-24 rounded-2xl bg-gray-100 animate-pulse mb-4" />
          <div className="h-40 rounded-2xl bg-gray-100 animate-pulse" />
        </div>
      </div>
    );
  }

  if (!ws) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <Compass className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h1 className="font-serif text-3xl font-bold text-habita-dark mb-2">Tu decisión te está esperando</h1>
          <p className="text-gray-500 mb-6">Comienza explorando algunas propiedades para que podamos acompañar tu proceso.</p>
          <Link to="/buscar" className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-habita text-white font-semibold text-sm hover:opacity-90">
            Empezar a explorar <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const s = ws.summary;
  const hasCompare = ws.active_comparisons > 0;
  const hasRec = ws.active_recommendations > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Mi decisión"
        description="Tu espacio personal en habita.co: revisa tus recomendaciones, favoritos y próximos pasos junto con el Habita Advisor."
        noindex
      />
      <Header />
      <div className="max-w-4xl mx-auto px-4 lg:px-6 py-8">
        {/* Memory banner (Fase 5.2 · ACT) — sólo si restauró un ctx antiguo */}
        {advisorMemory && (
          <div
            className="mb-6 rounded-2xl border border-habita/20 bg-habita-mint/50 p-4 flex items-center gap-3"
            data-testid="advisor-memory-banner"
          >
            <div className="w-9 h-9 rounded-full bg-habita text-white grid place-items-center shrink-0">
              <Compass className="w-4 h-4" />
            </div>
            <p className="text-sm text-habita-dark leading-snug">
              <span className="font-semibold">Bienvenido de vuelta.</span> Retomamos tu contexto donde lo dejaste
              {typeof advisorMemory.stale_days === 'number' && advisorMemory.stale_days > 0
                ? ` — hace ${advisorMemory.stale_days} día${advisorMemory.stale_days !== 1 ? 's' : ''}`
                : ''}
              . Puedes ajustar cualquier respuesta cuando quieras.
            </p>
          </div>
        )}

        {/* Decisión firme (Fase 5.1 · CONVERGE) */}
        {firmPick && (
          <div className="mb-6">
            <FirmPickCard firmPick={firmPick} onDelete={_handleDeleteFirmPick} />
          </div>
        )}

        {/* Hero — mensaje humano ANTES de números */}
        <div className="mb-8" data-testid="workspace-hero">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-full bg-habita text-white grid place-items-center">
              <Compass className="w-4 h-4" />
            </div>
            <p className="text-[11px] uppercase tracking-widest text-habita-dark/70 font-semibold">
              Habita Advisor · Mi decisión
            </p>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-habita-dark leading-tight" data-testid="workspace-headline">
            {ws.headline}
          </h1>
          <p className="text-sm text-gray-500 mt-2">
            Este es tu espacio para entender tu proceso y tomar decisiones con mayor claridad.
          </p>
        </div>

        {/* Números — DESPUÉS del mensaje humano */}
        <div className="mb-8 grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="workspace-summary">
          {[
            ['Búsquedas', s.searches],
            ['Propiedades vistas', s.properties_viewed],
            ['Favoritas', s.favorites],
            ['Comparaciones', s.comparisons],
            ['Contactos', s.contacts],
          ].map(([label, val]) => (
            <div key={label} className="bg-white border border-gray-200 rounded-2xl p-3">
              <p className="text-[10px] uppercase tracking-widest text-gray-500">{label}</p>
              <p className="mt-1 font-serif text-2xl font-bold text-habita-dark">{val}</p>
            </div>
          ))}
        </div>

        {/* Decision Quality (R0.7) — ¿cómo va tu proceso? */}
        {quality && (
          <div className="mb-8" data-testid="quality-slot">
            <DecisionQualityPanel quality={quality} />
          </div>
        )}

        {/* Companion — micro-interacción determinista (R0.6). Máx 1 visible. */}
        {companionPrompt && (
          <div className="mb-8" data-testid="companion-slot">
            <CompanionCard
              prompt={companionPrompt}
              onResolved={_onCompanionResolved}
            />
          </div>
        )}

        {/* Grid: Timeline + Progress */}
        <div className="grid lg:grid-cols-5 gap-6 mb-8">
          <div className="lg:col-span-3 bg-white border border-gray-200 rounded-2xl p-5">
            <h2 className="font-serif text-xl font-semibold text-habita-dark mb-4">Tu recorrido</h2>
            <Timeline phases={ws.timeline} />
            {!ws.timeline?.length && (
              <p className="text-sm text-gray-500">Aún no tenemos suficiente historia — sigue explorando y te contaremos tu recorrido.</p>
            )}
          </div>
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-2xl p-5">
            <h2 className="font-serif text-xl font-semibold text-habita-dark mb-1">Lo que ya tienes claro</h2>
            <p className="text-xs text-gray-500 mb-4">Refina cualquier respuesta cuando quieras — Habita Advisor sigue contigo.</p>
            <ProgressList items={ws.progress} onEdit={_onEditAspect} />
          </div>
        </div>

        {/* Recomendaciones y comparaciones activas */}
        {(hasRec || hasCompare) && (
          <div className="grid sm:grid-cols-2 gap-4">
            {hasRec && (
              <Link
                to="/buscar"
                onClick={() => _reportEvent('recommendation_opened_from_workspace')}
                className="group bg-white border border-habita/20 rounded-2xl p-5 hover:border-habita transition"
              >
                <div className="flex items-center gap-2 mb-2 text-habita">
                  <Sparkles className="w-4 h-4" />
                  <p className="text-[11px] uppercase tracking-widest font-semibold">
                    Recomendaciones activas
                  </p>
                </div>
                <p className="text-lg font-semibold text-habita-dark">Tenemos {ws.active_recommendations} sugerencia{ws.active_recommendations !== 1 ? 's' : ''} preparada{ws.active_recommendations !== 1 ? 's' : ''} para ti.</p>
                <p className="mt-1 text-xs text-gray-500 group-hover:text-habita">Ver recomendaciones →</p>
              </Link>
            )}
            {hasCompare && (
              <Link
                to="/buscar"
                onClick={() => _reportEvent('comparison_opened_from_workspace')}
                className="group bg-white border border-gray-200 rounded-2xl p-5 hover:border-habita transition"
              >
                <p className="text-[11px] uppercase tracking-widest text-habita-dark/70 font-semibold mb-2">Comparaciones</p>
                <p className="text-lg font-semibold text-habita-dark">Ya has hecho {ws.active_comparisons} comparación{ws.active_comparisons !== 1 ? 'es' : ''}.</p>
                <p className="mt-1 text-xs text-gray-500 group-hover:text-habita">Volver a comparar →</p>
              </Link>
            )}
          </div>
        )}

        <p className="mt-10 text-[10px] text-gray-400 text-center">
          Habita Advisor · Construido a partir de tu recorrido — determinístico, sin IA generativa
        </p>
      </div>
    </div>
  );
}
